import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
  Card as ChakraCard,
  Tooltip,
  Icon,
} from '@chakra-ui/react'
import { Dispatch, SetStateAction, forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import Card from '../../../common/components/Card'
import UpgradeBadge from '../../../components/UpgradeBadge'
import { usePaywall } from '../../../hooks/usePaywall'
import {
  ProductResult,
  ProductSearchClient,
  clearProductSearch,
  resetProductResults,
  selectProduct,
  setProductResults,
  setSearchTerm,
} from '../ScriptForm/ProductSearchContextSlice'
import {
  commitEhrPrescribedItem,
  resetPrescribedItemByIndex,
  selectPrescribedItem,
} from '../ScriptForm/PrescribedItem/PrescribedItemSlice'
import { DrugSearchResultsPanel } from '../DrugSearchResultsPanel'
import useApi from '../../../common/utils/api/useApi'
import { FormControl, FormLabel } from '@chakra-ui/form-control'
import { customPrescriptionItemToPrescribedItem } from '../ScriptForm/PrescribedItem'
import { PrescribedItem } from '../../../types'
import { CustomPrescriptionItemModal } from '../CustomPrescriptionItem/CustomPrescriptionItemModal'
import { initFormError, QUANTITY_MUST_BE_GREATER_THAN_ZERO_MESSAGE } from '../constants'
import MimsIntegrated from '../../../assets/mims-integrated.png'
import { prepareMimsPrescribedItem } from '../ScriptForm/PrescribedItem/utils/prepareMimsPrescribedItem'
import PrescribedItemDetails from './components/PrescribedItemDetails'
import { getPbsStatus } from './utils/getPbsStatus'
import { isQuantityMissing } from './utils/isQuantityMissing'
import {
  DEFAULT_DRUG_SEARCH_PAGE,
  isSearchableDrugTerm,
  useDrugSearchPaging,
} from './utils/useDrugSearchPaging'
import { PrescribedItemErrors } from '../../../types/prescription'
import { RootState } from '../../../app/store'
import { AddIcon, SmallCloseIcon, WarningIcon } from '@chakra-ui/icons'
import { FundingSource } from '../../../types/organization'
import { nanoid } from '@reduxjs/toolkit'
import { useHasEntitlement } from '../../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../../types/organization-entitlements'
import {
  beginEhrProductSelection,
  cancelEhrReprescribingSeed,
  completeEhrProductSelection,
  failEhrProductSelection,
} from '../../../features/ehr-launch/ehrRecentMedicationRequestsSlice'

interface PrescriptionListProps {
  scid?: string
  prescribedItems: (PrescribedItem | undefined)[]
  toggleDrawer?: () => void
  onDelete?: (index: number) => void
  onAdd: (item?: PrescribedItem) => any
  onUpdate: (item: PrescribedItem, index: number) => any
  formErrors: Record<string, PrescribedItemErrors>
  setFormErrors: Dispatch<SetStateAction<Record<string, PrescribedItemErrors>>>
  isAbleToSelectS8Medicine: boolean
  isOnlyPrivateMedicationAllowed: boolean
}

const PrescribedItemList = forwardRef<HTMLDivElement, PrescriptionListProps>((props, ref) => {
  const { prescribedItems, onAdd, onDelete, formErrors } = props
  const tabRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const { selectedPrescribedItemIndex } = useAppSelector((state) => state.newScript)
  const pendingSeed = useAppSelector((state) => state.ehrRecentMedicationRequests.pendingSeed)
  const isMultipleScriptsEnabled = useHasEntitlement(FeatureKey.MULTIPLE_SCRIPTS)
  const { hasSubscriptionError, open: openPaywall, paywallModal } = usePaywall()

  const shouldShowPaywall =
    hasSubscriptionError || (!isMultipleScriptsEnabled && prescribedItems.length >= 1)

  const handleAddMedication = () => {
    if (shouldShowPaywall) {
      openPaywall()
      return
    }
    onAdd()
    dispatch(selectPrescribedItem(prescribedItems.length))

    setTimeout(() => {
      tabRef.current?.scrollTo({ left: tabRef.current?.scrollWidth, behavior: 'smooth' })
    }, 100)
  }

  const handleTabChange = (index: number) => {
    if (index === prescribedItems.length) {
      handleAddMedication()
      return
    }
    if (pendingSeed && pendingSeed.slotIndex !== index) {
      dispatch(cancelEhrReprescribingSeed())
      dispatch(clearProductSearch())
    }
    dispatch(selectPrescribedItem(index))
  }

  const handleDeleteScript = () => {
    if (pendingSeed?.slotIndex === selectedPrescribedItemIndex) {
      dispatch(cancelEhrReprescribingSeed())
      dispatch(clearProductSearch())
    }
    onDelete?.(selectedPrescribedItemIndex)
    const newLength = prescribedItems.length - 1
    dispatch(
      selectPrescribedItem(
        selectedPrescribedItemIndex <= newLength - 1
          ? selectedPrescribedItemIndex
          : Math.max(0, selectedPrescribedItemIndex - 1)
      )
    )
  }

  return (
    <VStack ref={ref} alignItems="stretch" tabIndex={-1}>
      <Heading as="h4" size="md">
        Prescription Summary
      </Heading>
      {paywallModal}
      <Card p="16px">
        <Tabs
          variant="enclosed-colored"
          onChange={handleTabChange}
          index={selectedPrescribedItemIndex}
        >
          <Flex
            maxWidth="100%"
            overflow="auto"
            h="62px"
            alignItems="end"
            ref={tabRef}
            overflowY="hidden"
            pb="2px"
          >
            <TabList>
              {prescribedItems.map((item, index) => (
                <Tooltip key={index} label={item?.displayName}>
                  <Tab
                    whiteSpace="nowrap"
                    role="group"
                    _hover={{
                      '& .tab-close-icon': {
                        visibility: 'visible',
                      },
                    }}
                    display="flex"
                    alignItems="center"
                    maxHeight="100%"
                  >
                    {item?.productId &&
                      (formErrors?.[item.productId]?.patientInstructions?.showError ||
                        formErrors?.[item.productId]?.reasonForPrescribing?.showError ||
                        formErrors?.[item.productId]?.quantity?.showError ||
                        formErrors?.[item.productId]?.pbsAuthority?.showError) && (
                        <Icon as={WarningIcon} color="red.500" mr={2} />
                      )}
                    <Text maxW="120px" overflow="hidden" textOverflow="ellipsis">
                      {item?.displayName || `Medication ${index + 1}`}
                    </Text>
                    {prescribedItems.length > 1 && (
                      <SmallCloseIcon
                        ml={2}
                        visibility="hidden"
                        className="tab-close-icon"
                        _hover={{
                          backgroundColor: 'rgba(249, 112, 112, 0.1)',
                          borderRadius: 'full',
                          cursor: 'pointer',
                          color: 'red.500',
                        }}
                        onClick={() => handleDeleteScript()}
                      />
                    )}
                  </Tab>
                </Tooltip>
              ))}
              {prescribedItems.length < 15 && (
                <Tab whiteSpace="nowrap" position="relative">
                  <AddIcon boxSize={3} />
                  <Text ml={2}>Add Medication</Text>
                  {shouldShowPaywall && <UpgradeBadge />}
                </Tab>
              )}
            </TabList>
          </Flex>
          <TabPanels>
            {prescribedItems.map((item, index) => (
              <TabPanel key={index}>
                <AddDrugDetails {...props} medication={item} />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card>
    </VStack>
  )
})

PrescribedItemList.displayName = 'PrescribedItemList'

const AddDrugDetails: React.FC<
  PrescriptionListProps & {
    medication: PrescribedItem | undefined
  }
> = ({
  scid,
  toggleDrawer,
  prescribedItems,
  onUpdate,
  formErrors,
  setFormErrors,
  isAbleToSelectS8Medicine,
  isOnlyPrivateMedicationAllowed,
  medication,
}) => {
  const { productResults, productSearchTerm, selectedProduct } = useAppSelector(
    (state) => state.product
  )
  const { selectedPrescribedItemIndex } = useAppSelector((state) => state.newScript)
  const ehrReviewSlotIndexes = useAppSelector((state) => state.newScript.ehrReviewSlotIndexes)
  const pendingSeed = useAppSelector((state) => state.ehrRecentMedicationRequests.pendingSeed)
  const pendingSeedRef = useRef(pendingSeed)
  pendingSeedRef.current = pendingSeed
  useEffect(
    () => () => {
      pendingSeedRef.current = undefined
    },
    []
  )
  const isDeactivatedPrescriber: boolean = useAppSelector(
    ({ prescriber }: RootState) => !prescriber?.prescriber?.active
  )
  const defaultOrganizationFoundingSource = useAppSelector(
    (state: RootState) => state.organization?.data?.settings?.defaultFundingSource
  )
  const [hasMoreDrugs, setHasMoreDrugs] = useState(false)

  const selectedPrescribedItem = prescribedItems[selectedPrescribedItemIndex ?? -1]

  const dispatch = useAppDispatch()
  const toast = useToast()

  const { trigger: getProductDetails } = useApi(ProductSearchClient.getProductDetails)

  const { selectedPatient } = useAppSelector((state) => state.patientDetail)

  // Check if product contains S8 controlled substance
  const checkS8Restriction = (poisonClasses?: string[]): boolean => {
    if (!isAbleToSelectS8Medicine && poisonClasses?.includes('S8')) {
      toast({
        title: 'Warning',
        description: 'Selection of S8 controlled substance is prohibited.',
        status: 'warning',
        duration: null,
        isClosable: true,
        position: 'top',
      })
      return false
    }
    return true
  }

  const releaseFailedEhrSelection = (isEhrSelection: boolean, selectionAttemptId: string) => {
    if (
      !isEhrSelection ||
      pendingSeedRef.current?.slotIndex !== selectedPrescribedItemIndex ||
      pendingSeedRef.current.selectionAttemptId !== selectionAttemptId
    )
      return
    dispatch(
      failEhrProductSelection({ slotIndex: selectedPrescribedItemIndex, selectionAttemptId })
    )
    toast({
      title: 'Error',
      description: 'Medicine details could not be loaded. Choose a product and try again.',
      status: 'error',
      duration: null,
      isClosable: true,
      position: 'top',
    })
  }

  const createAndAddPrescription = async (product: ProductResult) => {
    const isEhrSelection = pendingSeed?.slotIndex === selectedPrescribedItemIndex
    const selectionAttemptId = nanoid()
    if (isEhrSelection) {
      dispatch(
        beginEhrProductSelection({ slotIndex: selectedPrescribedItemIndex, selectionAttemptId })
      )
    }
    try {
      const response = await getProductDetails({
        id: product.productId,
        productType: product.productType,
      })

      if (!response) {
        releaseFailedEhrSelection(isEhrSelection, selectionAttemptId)
        console.error('No response returned')
        return
      }

      if (
        isEhrSelection &&
        (pendingSeedRef.current?.slotIndex !== selectedPrescribedItemIndex ||
          pendingSeedRef.current.selectionAttemptId !== selectionAttemptId)
      )
        return

      const prepared = prepareMimsPrescribedItem(response.product, {
        ctgFlag: selectedPatient?.ctg_flag,
        isAbleToSelectS8Medicine,
        ehrPrefill: isEhrSelection ? pendingSeedRef.current?.prefill : undefined,
      })
      const { passed, message } = prepared
      if (message) {
        toast({
          title: passed ? 'Warning' : 'Error',
          description: message,
          status: passed ? 'warning' : 'error',
          duration: null,
          isClosable: true,
          position: 'top',
        })
      }
      if (!passed) {
        if (isEhrSelection) {
          dispatch(
            failEhrProductSelection({ slotIndex: selectedPrescribedItemIndex, selectionAttemptId })
          )
        }
        return
      }

      const item = prepared.item

      dispatch(selectProduct(undefined))
      dispatch(resetProductResults())
      dispatch(setSearchTerm(''))
      if (isEhrSelection) {
        dispatch(commitEhrPrescribedItem({ index: selectedPrescribedItemIndex, item }))
        dispatch(
          completeEhrProductSelection({
            slotIndex: selectedPrescribedItemIndex,
            selectionAttemptId,
          })
        )
      } else {
        onUpdate(item, selectedPrescribedItemIndex)
      }
      if (item && defaultOrganizationFoundingSource !== FundingSource.PRIVATE) {
        toggleDrawer?.()
      }
    } catch (e) {
      releaseFailedEhrSelection(isEhrSelection, selectionAttemptId)
      console.error(e)
    }
  }

  const searchAndSelectProduct = async (product: ProductResult) => {
    const isEhrSelection = pendingSeed?.slotIndex === selectedPrescribedItemIndex
    const selectionAttemptId = nanoid()
    if (isEhrSelection) {
      dispatch(
        beginEhrProductSelection({ slotIndex: selectedPrescribedItemIndex, selectionAttemptId })
      )
    }
    try {
      const response = await getProductDetails({
        id: product.productId,
        productType: product.productType,
      })

      if (!response) {
        releaseFailedEhrSelection(isEhrSelection, selectionAttemptId)
        return
      }

      if (
        isEhrSelection &&
        (pendingSeedRef.current?.slotIndex !== selectedPrescribedItemIndex ||
          pendingSeedRef.current.selectionAttemptId !== selectionAttemptId)
      )
        return

      // Check S8 restriction
      if (!checkS8Restriction(response?.product?.poisonClassification?.poisonClasses)) {
        if (isEhrSelection) {
          dispatch(
            failEhrProductSelection({ slotIndex: selectedPrescribedItemIndex, selectionAttemptId })
          )
        }
        return
      }

      if (isEhrSelection) {
        dispatch(
          failEhrProductSelection({ slotIndex: selectedPrescribedItemIndex, selectionAttemptId })
        )
      }
      return dispatch(selectProduct(response?.product))
    } catch (e) {
      releaseFailedEhrSelection(isEhrSelection, selectionAttemptId)
      console.error(e)
    }
  }

  const { trigger: searchProducts } = useApi(ProductSearchClient.searchProduct, {
    onSuccess: (response) => {
      setHasMoreDrugs(response.products?.length > 0)
      dispatch(setProductResults(response.products))
    },
  })

  const searchProductsRef = useRef(searchProducts)
  searchProductsRef.current = searchProducts

  const drugSearch = useMemo(
    () =>
      debounce((queryTerm: string) => {
        if (isSearchableDrugTerm(queryTerm)) {
          searchProductsRef.current({
            queryTerm,
            ...DEFAULT_DRUG_SEARCH_PAGE,
          })
        }
      }, 500),
    []
  )

  useEffect(() => () => drugSearch.cancel(), [drugSearch])

  useEffect(() => {
    if (medication?.productId && !formErrors[medication.productId]) {
      setFormErrors((prev) => ({
        ...prev,
        [medication.productId]: {
          ...initFormError,
          quantity: {
            showError: isQuantityMissing(medication),
            message: QUANTITY_MUST_BE_GREATER_THAN_ZERO_MESSAGE,
          },
        },
      }))
    }
    // Only run when medication.productId changes to initialize form errors
  }, [medication?.productId, formErrors, setFormErrors])

  const { loadMore: loadMoreDrugs, resetPaging } = useDrugSearchPaging(
    productSearchTerm,
    searchProducts
  )

  return (
    <Card p="16px">
      <VStack gap="16px" alignItems="stretch">
        {scid && <Text>SCID: {scid}</Text>}
        {medication && ehrReviewSlotIndexes.includes(selectedPrescribedItemIndex) && (
          <Alert status="info">
            <AlertIcon />
            Imported from EHR — review all details before prescribing
          </Alert>
        )}
        {!medication ? (
          <>
            <FormControl>
              <HStack justifyContent="start" alignItems="start">
                <FormLabel fontWeight="bold">Add Medications</FormLabel>
                <Image src={MimsIntegrated} alt="Mims Integrated" width="100px" />
              </HStack>
              <Input
                bgColor="white"
                placeholder="Search drugs ( at least 3 characters )"
                aria-label="Search drugs"
                value={productSearchTerm}
                disabled={isDeactivatedPrescriber}
                onChange={(e) => {
                  const queryTerm = e.target.value
                  dispatch(resetProductResults())
                  dispatch(setSearchTerm(queryTerm))
                  drugSearch(queryTerm)
                  resetPaging()
                  if (selectedProduct) dispatch(selectProduct(undefined))
                }}
              ></Input>
            </FormControl>

            {productSearchTerm.length > 0 && (
              <DrugSearchResultsPanel
                hasMore={hasMoreDrugs}
                loadMoreResults={loadMoreDrugs}
                results={productResults}
                onAdd={async (product) => {
                  await createAndAddPrescription(product)
                }}
                onSelect={async (product) => {
                  toggleDrawer?.()
                  await searchAndSelectProduct(product)
                }}
                selectedId={selectedProduct?.productId}
                selectionDisabled={!!pendingSeed?.selectionAttemptId}
              />
            )}
            <CustomPrescriptionItemModal
              onSelect={(item) => {
                if (pendingSeed?.slotIndex === selectedPrescribedItemIndex) {
                  dispatch(cancelEhrReprescribingSeed())
                  dispatch(clearProductSearch())
                }
                const customPrescriptionItem = customPrescriptionItemToPrescribedItem(
                  item,
                  {},
                  selectedPatient?.ctg_flag
                )
                dispatch(selectProduct(undefined))
                return onUpdate(customPrescriptionItem, selectedPrescribedItemIndex)
              }}
              isOnlyPrivateMedicationAllowed={isOnlyPrivateMedicationAllowed}
            >
              {({ openModal }) => (
                <Box>
                  <Button
                    colorScheme="blue"
                    onClick={openModal}
                    variant="outline"
                    disabled={isDeactivatedPrescriber}
                  >
                    + Custom Preparation Items
                  </Button>
                </Box>
              )}
            </CustomPrescriptionItemModal>
          </>
        ) : (
          <>
            <MedicationSummaryCard medication={medication} toggleDrawer={toggleDrawer} />
            <PrescribedItemDetails
              prescribedItem={medication}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              onChange={(updatedItem) => {
                const selectedItemIndex = prescribedItems.findIndex(
                  (item) => selectedPrescribedItem === item
                )
                if (selectedItemIndex !== -1) {
                  onUpdate(updatedItem, selectedItemIndex)
                }
              }}
              onSelectPBSOrRPBS={() => toggleDrawer && toggleDrawer()}
              isOnlyPrivateMedicationAllowed={isOnlyPrivateMedicationAllowed}
            />
          </>
        )}
      </VStack>
    </Card>
  )
}

const MedicationSummaryCard = ({
  medication,
  toggleDrawer,
}: {
  medication: PrescribedItem
  toggleDrawer?: () => void
}) => {
  const dispatch = useAppDispatch()
  const selectedPrescribedItemIndex = useAppSelector(
    (state) => state.newScript.selectedPrescribedItemIndex
  )
  return (
    <ChakraCard
      bgColor="green.50"
      borderColor="green.400"
      borderWidth="1px"
      data-testid="selected-medication"
      padding="4"
      pr="0"
      borderRadius="md"
      position="relative"
      gap="2"
      cursor="pointer"
      onClick={() => {
        toggleDrawer?.()
      }}
    >
      <Flex gap="4" alignItems="center" flexWrap="wrap">
        <Badge variant="solid" colorScheme="green">
          SELECTED
        </Badge>
        <Text flex="1" fontWeight="bold">
          {medication.displayName}
        </Text>
        <IconButton
          position="absolute"
          right="0"
          top="0"
          zIndex="10"
          variant="unstyled"
          colorScheme="green"
          aria-label="Remove medication"
          icon={<SmallCloseIcon color="gray.500" />}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            dispatch(resetPrescribedItemByIndex(selectedPrescribedItemIndex))
          }}
        />
      </Flex>

      <Flex gap="4" color="gray.700">
        <Flex>
          <b>Qty</b>: <Text color="gray.500">{medication.quantity}</Text>
        </Flex>
        <Flex>
          <b>Rpts</b>: <Text color="gray.500">{medication.numberOfRepeatsAuthorised}</Text>
        </Flex>
        <Flex>
          <b>PBS Status</b>: <Text color="gray.500">{getPbsStatus(medication)}</Text>
        </Flex>
      </Flex>
    </ChakraCard>
  )
}

export default PrescribedItemList
